// File: /src/components/KanbanBoard.js

import React, { useState, useMemo, useEffect } from 'react';
import {
	DndContext,
	closestCenter,
	pointerWithin,
	PointerSensor,
	TouchSensor,
	useSensor,
	useSensors,
	DragOverlay,
} from '@dnd-kit/core';
import { toast } from 'react-toastify';
import apiClient from '../services/apiClient';
import { useProjectsData } from '../hooks/useProjectsData';
import KanbanColumn from './KanbanColumn';
import KanbanCard from './KanbanCard';
import '../styles/KanbanBoard.css';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
	style: 'currency',
	currency: 'BRL',
	maximumFractionDigits: 2,
});

const KanbanBoard = () => {
	const {
		projects,
		clients,
		loading,
		error,
		setProjectsCache,
	} = useProjectsData();
	const [activeId, setActiveId] = useState(null);
	const [lastMoved, setLastMoved] = useState(null);
	const [selectedClientId, setSelectedClientId] = useState('all');
	const [searchTerm, setSearchTerm] = useState('');
	const hasProjects = (projects?.length ?? 0) > 0;
	const isInitialLoading = loading && !hasProjects;

	// Configurar sensores para drag and drop
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8, // Previne drag acidental ao clicar
			},
		}),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: 150,
				tolerance: 10,
			},
		})
	);

	const filteredProjects = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		return (projects || []).filter((project) => {
			const matchesClient =
				selectedClientId === 'all' ||
				String(project.clientId) === selectedClientId;
			const matchesTerm =
				!term ||
				project.name?.toLowerCase().includes(term) ||
				project.description?.toLowerCase().includes(term);
			return matchesClient && matchesTerm;
		});
	}, [projects, selectedClientId, searchTerm]);

	// Colunas do Kanban
	const columns = [
		{ id: 'pending', title: 'Pendente', color: '#94a3b8' },
		{ id: 'in_progress', title: 'Em Andamento', color: '#3b82f6' },
		{ id: 'completed', title: 'Concluído', color: '#10b981' },
	];

	// Filtrar projetos por status e ordenar (último movido no topo)
	const projectsByStatus = useMemo(() => {
		const grouped = {
			pending: [],
			in_progress: [],
			completed: [],
		};

		filteredProjects.forEach((project) => {
			if (grouped[project.status]) {
				grouped[project.status].push(project);
			}
		});

		if (lastMoved && grouped[lastMoved.status]) {
			const column = grouped[lastMoved.status];
			const idx = column.findIndex((p) => p.id === lastMoved.id);
			if (idx > 0) {
				const [movedProject] = column.splice(idx, 1);
				column.unshift(movedProject);
			}
		}

		return grouped;
	}, [filteredProjects, lastMoved]);

	const columnSummaries = useMemo(() => {
		const base = {
			pending: { count: 0, totalValue: 0, leadTimeSum: 0 },
			in_progress: { count: 0, totalValue: 0, leadTimeSum: 0 },
			completed: { count: 0, totalValue: 0, leadTimeSum: 0 },
		};

		filteredProjects.forEach((project) => {
			const target = base[project.status];
			if (!target) return;

			target.count += 1;
			const numericValue = Number(project.value) || 0;
			target.totalValue += numericValue;

			const start = project.start_date ? new Date(project.start_date) : null;
			const end = project.end_date ? new Date(project.end_date) : null;
			if (start && end && !Number.isNaN(start) && !Number.isNaN(end)) {
				const diffMs = Math.max(end - start, 0);
				target.leadTimeSum += diffMs / (1000 * 60 * 60 * 24);
			}
		});

		return Object.keys(base).reduce((acc, key) => {
			const node = base[key];
			acc[key] = {
				count: node.count,
				totalValue: node.totalValue,
				averageLeadTime: node.count ? node.leadTimeSum / node.count : 0,
			};
			return acc;
		}, {});
	}, [filteredProjects]);

	// Handler para drag start
	const handleDragStart = (event) => {
		setActiveId(event.active.id);
	};

	// Handler para drag end
	const handleDragEnd = async (event) => {
		const { active, over } = event;

		if (!over) return;

		const projectId = active.id;
		
		// Se soltou sobre um card, precisamos encontrar a coluna desse card
		// Se soltou sobre a coluna, over.id já é o status
		let newStatus = over.id;
		
		// Se over.id for um número (ID de projeto), buscar o status desse projeto
		const overProject = projects.find((p) => p.id === over.id);
		if (overProject) {
			newStatus = overProject.status;
		}

		// Encontrar o projeto sendo movido
		const project = projects.find((p) => p.id === projectId);
		if (!project || project.status === newStatus) return;

		// Marcar como último movido para colocar no topo
		setLastMoved({ id: projectId, status: newStatus });

		// Atualizar localmente (otimistic update)
		setProjectsCache((prevProjects = []) =>
			prevProjects.map((p) =>
				p.id === projectId ? { ...p, status: newStatus } : p
			)
		);

		// Atualizar no backend
		try {
			await apiClient.patch(`/projects/${projectId}/status`, {
				status: newStatus,
			});
			toast.success('Status do projeto atualizado com sucesso!');
		} catch (err) {
			// Reverter em caso de erro
			setProjectsCache((prevProjects = []) =>
				prevProjects.map((p) =>
					p.id === projectId ? { ...p, status: project.status } : p
				)
			);
			toast.error('Erro ao atualizar status do projeto.');
		} finally {
			setActiveId(null);
		}
	};

	// Handler para drag cancel
	const handleDragCancel = () => {
		setActiveId(null);
	};

	// Encontrar projeto ativo
	const activeProject = activeId ? (projects || []).find((p) => p.id === activeId) : null;

	// Detecção de colisão customizada que prioriza colunas
	const customCollisionDetection = (args) => {
		// Primeiro tenta encontrar a coluna usando pointerWithin
		const pointerCollisions = pointerWithin(args);
		const columnCollisions = pointerCollisions.filter(
			({ id }) => ['pending', 'in_progress', 'completed'].includes(id)
		);

		if (columnCollisions.length > 0) {
			return columnCollisions;
		}

		// Se não encontrou coluna, usa closestCenter
		return closestCenter(args);
	};

	useEffect(() => {
		if (error) {
			toast.error('Erro ao carregar projetos.');
		}
	}, [error]);

	if (isInitialLoading) {
		return (
			<div className="kanban-loading">
				<div className="spinner"></div>
				<p>Carregando projetos...</p>
			</div>
		);
	}

	return (
		<div className="kanban-container">
			<div className="kanban-header">
				<h1>Quadro Kanban</h1>
				<p className="kanban-subtitle">
					Arraste os projetos entre as colunas para atualizar o status
				</p>
				<div className="kanban-controls">
					<div className="kanban-filter">
						<label htmlFor="kanban-filter-client">Cliente</label>
						<select
							id="kanban-filter-client"
							value={selectedClientId}
							onChange={(event) =>
								setSelectedClientId(event.target.value)
							}
						>
							<option value="all">Todos</option>
							{(clients || []).map((client) => (
								<option key={client.id} value={String(client.id)}>
									{client.name}
								</option>
							))}
						</select>
					</div>
					<div className="kanban-filter">
						<label htmlFor="kanban-filter-search">Busca</label>
						<input
							id="kanban-filter-search"
							type="text"
							placeholder="Nome ou descrição"
							value={searchTerm}
							onChange={(event) => setSearchTerm(event.target.value)}
						/>
					</div>
					{loading && hasProjects && (
						<span className="kanban-sync-indicator">
							Sincronizando...
						</span>
					)}
				</div>
				{!loading && hasProjects && filteredProjects.length === 0 && (
					<p className="kanban-empty-filters">
						Nenhum projeto corresponde aos filtros selecionados.
					</p>
				)}
			</div>

			<DndContext
				sensors={sensors}
				collisionDetection={customCollisionDetection}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
				onDragCancel={handleDragCancel}
			>
				<div className="kanban-board">
					{columns.map((column) => {
						const columnProjects = projectsByStatus[column.id] || [];
						const columnSummary = columnSummaries[column.id] || {
							count: 0,
							totalValue: 0,
							averageLeadTime: 0,
						};
						return (
							<KanbanColumn
								key={column.id}
								id={column.id}
								title={column.title}
								color={column.color}
								count={columnProjects.length}
								totalValueLabel={currencyFormatter.format(
									columnSummary.totalValue,
								)}
								averageLeadTimeLabel={
									columnSummary.count > 0
										? `${Math.round(
												columnSummary.averageLeadTime,
										  )} dia(s)`
										: 'Sem dados'
								}
							>
								{columnProjects.length === 0 ? (
									<div className="empty-column">
										<p>Nenhum projeto</p>
									</div>
								) : (
									columnProjects.map((project) => (
										<KanbanCard key={project.id} project={project} />
									))
								)}
							</KanbanColumn>
						);
					})}
				</div>

				<DragOverlay dropAnimation={null}>
					{activeProject ? (
						<KanbanCard project={activeProject} />
					) : null}
				</DragOverlay>
			</DndContext>
		</div>
	);
};

export default KanbanBoard;
